import React, { useState, useEffect, useRef } from 'react';
import { Socket } from 'socket.io-client';
import { Users, MessageSquare, Video, ArrowLeft, UserPlus, Copy, X, Loader2 } from 'lucide-react';
import { User } from '../types';
import { storage } from '../storage';
import { buildRoomInviteLink } from '../deepLink';
import {
  changeOnlineRole,
  getOnlineRoomState,
  joinOnlineRoom,
  leaveOnlineRoom,
  OnlineChatMsg,
  OnlineRoomUser,
  OnlineRole,
  OnlineSignalMsg,
  resetOnlineBattle,
  sendOnlineSignal,
  sendOnlineRoomChat,
  startOnlineBattle,
} from './onlineHttpApi';

const WeChatIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178A1.17 1.17 0 0 1 4.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178 1.17 1.17 0 0 1-1.162-1.178c0-.651.52-1.18 1.162-1.18zm5.34 2.867c-1.797-.052-3.746.512-5.28 1.786-1.72 1.428-2.687 3.72-1.78 6.22.942 2.453 3.666 4.229 6.884 4.229.826 0 1.622-.12 2.361-.336a.722.722 0 0 1 .598.082l1.584.926a.272.272 0 0 0 .14.047c.134 0 .24-.111.24-.247 0-.06-.023-.12-.038-.177l-.327-1.233a.582.582 0 0 1-.023-.156.49.49 0 0 1 .201-.398C23.024 18.48 24 16.82 24 14.98c0-3.21-2.931-5.837-6.656-6.088V8.89c-.135-.01-.27-.027-.407-.031zm-2.53 3.274c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.97-.982zm4.844 0c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.969-.982z"/>
  </svg>
);

interface VideoBattleProps {
  user: User;
  onBack: () => void;
  roomId: string;
  roomPassword?: string;
  socket: Socket | null;
  transportMode: 'socket' | 'http';
  sessionId?: string;
}

// 身份切换 / 获胜投票 / 开始比赛这类操作都需要一次网络往返，
// 原生 App 弱网下会明显延迟，用待处理态给用户即时反馈。
type PendingAction = 'role' | 'vote' | 'start' | 'reset';

const reportDebugEvent = (hypothesisId: 'A' | 'B' | 'C' | 'D', location: string, msg: string, data: Record<string, unknown> = {}) => {
  void hypothesisId; void location; void msg; void data;
};

export const VideoBattle: React.FC<VideoBattleProps> = ({ user, onBack, roomId, roomPassword, socket, transportMode, sessionId }) => {
  const [users, setUsers] = useState<OnlineRoomUser[]>([]);
  const [chat, setChat] = useState<OnlineChatMsg[]>([]);
  const [msgInput, setMsgInput] = useState('');
  const [myRole, setMyRole] = useState<OnlineRole>('AUDIENCE');
  const [amIHost, setAmIHost] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [battleActive, setBattleActive] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraStarting, setCameraStarting] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [videoInputs, setVideoInputs] = useState<MediaDeviceInfo[]>([]);
  const [selectedVideoDeviceId, setSelectedVideoDeviceId] = useState('');
  const [showCameraMenu, setShowCameraMenu] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  // 开始比赛/投票/结束时如果失败，直接把错误显示在房间里，避免只弹一次 alert 被忽略
  const [actionError, setActionError] = useState<string | null>(null);
  
  const myVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef1 = useRef<HTMLVideoElement>(null);
  const remoteVideoRef2 = useRef<HTMLVideoElement>(null);
  
  const peerConnections = useRef<Map<string, RTCPeerConnection>>(new Map());
  const localStream = useRef<MediaStream | null>(null);
  // WebRTC：远端流 / 已发起 offer 的对手 / 收到但暂时无法应用的 ICE 候选 / 已处理的信令 id
  const remoteStreamsRef = useRef<Map<string, MediaStream>>(new Map());
  const offeredPeersRef = useRef<Set<string>>(new Set());
  const pendingCandidatesRef = useRef<Map<string, RTCIceCandidateInit[]>>(new Map());
  const processedSignalIdsRef = useRef<Set<string>>(new Set());
  const [remoteConnected, setRemoteConnected] = useState(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const onBackRef = useRef(onBack);
  const deleteRoomInProgressRef = useRef(false);
  const pendingTimerRef = useRef<number | null>(null);
  // 身份切换后立即拉一次房间状态，避免按钮/成员角色要等下一轮 3 秒轮询才更新。
  const roomRefreshRef = useRef<(() => Promise<void>) | null>(null);
  // 记录按下位置：部分 WebView 里合成 click 可能丢失，用 pointerup 兜底触发按钮
  const pressStartRef = useRef<{ x: number; y: number } | null>(null);
  // 临时诊断：记录按钮收到的指针/点击事件与当时状态（排查"点了没反应"）
  const recordStartProbe = (stage: string) => {
    try {
      const key = 'kendama-start-probe';
      const list = JSON.parse(window.localStorage.getItem(key) || '[]');
      list.push({
        ts: Date.now(),
        stage,
        canStartBattle,
        players: players.length,
        pending: pendingAction,
        isAdmin: Boolean(user.isAdmin),
        amIHost,
        roles: user.roles || null,
        transportMode,
        hasSessionId: Boolean(sessionId),
        hasSocket: Boolean(socket),
      });
      window.localStorage.setItem(key, JSON.stringify(list.slice(-20)));
    } catch {
      // 忽略
    }
  };
  // 记录正在等待服务器确认的操作，避免 3 秒轮询用旧数据把乐观更新覆盖回去。
  const actionInFlightRef = useRef<PendingAction | null>(null);
  const userEmail = user.email;
  const userDisplayName = user.displayName;
  const userUid = user.uid;

  useEffect(() => {
    onBackRef.current = onBack;
  }, [onBack]);

  const endPendingAction = () => {
    if (pendingTimerRef.current !== null) {
      window.clearTimeout(pendingTimerRef.current);
      pendingTimerRef.current = null;
    }
    actionInFlightRef.current = null;
    setPendingAction(null);
  };

  const beginPendingAction = (action: PendingAction) => {
    actionInFlightRef.current = action;
    setPendingAction(action);
    if (pendingTimerRef.current !== null) window.clearTimeout(pendingTimerRef.current);
    // 兜底：socket 广播丢失或请求悬挂时不至于一直卡在禁用态。
    pendingTimerRef.current = window.setTimeout(endPendingAction, 8000);
  };

  const finishPendingAction = (action: PendingAction) => {
    if (actionInFlightRef.current !== action) return;
    endPendingAction();
  };

  useEffect(() => () => {
    if (pendingTimerRef.current !== null) window.clearTimeout(pendingTimerRef.current);
  }, []);

  const reportDebugEvent = (hypothesisId: 'A' | 'B' | 'C' | 'D', location: string, msg: string, data: Record<string, unknown> = {}) => {
    void hypothesisId; void location; void msg; void data;
  };

  const bindStreamToVideo = async (videoElement: HTMLVideoElement | null, stream: MediaStream | null) => {
    // #region debug-point B:bind-stream
    reportDebugEvent('B', 'VideoBattle.tsx:bindStreamToVideo', 'binding stream to video element', {
      hasVideoElement: Boolean(videoElement),
      hasStream: Boolean(stream),
      streamId: stream?.id ?? null,
      videoTrackCount: stream?.getVideoTracks().length ?? 0,
      audioTrackCount: stream?.getAudioTracks().length ?? 0
    });
    // #endregion
    if (!videoElement) return;
    videoElement.srcObject = stream;
    if (!stream) return;

    videoElement.muted = true;
    videoElement.autoplay = true;
    videoElement.playsInline = true;

    const tryPlay = async () => {
      try {
        await videoElement.play();
        // #region debug-point B:play-resolved
        reportDebugEvent('B', 'VideoBattle.tsx:bindStreamToVideo', 'video element play resolved', {
          readyState: videoElement.readyState,
          paused: videoElement.paused,
          currentTime: videoElement.currentTime,
          videoWidth: videoElement.videoWidth,
          videoHeight: videoElement.videoHeight
        });
        // #endregion
      } catch (error) {
        console.warn('Local video autoplay was blocked:', error);
        // #region debug-point B:play-failed
        reportDebugEvent('B', 'VideoBattle.tsx:bindStreamToVideo', 'video element play rejected', {
          errorName: error instanceof Error ? error.name : 'unknown',
          errorMessage: error instanceof Error ? error.message : String(error),
          readyState: videoElement.readyState,
          paused: videoElement.paused
        });
        // #endregion
      }
    };

    videoElement.onloadedmetadata = () => {
      // #region debug-point B:loaded-metadata
      reportDebugEvent('B', 'VideoBattle.tsx:bindStreamToVideo', 'video loadedmetadata fired', {
        readyState: videoElement.readyState,
        videoWidth: videoElement.videoWidth,
        videoHeight: videoElement.videoHeight,
        currentTime: videoElement.currentTime
      });
      // #endregion
      void tryPlay();
    };

    void tryPlay();
  };

  const formatCameraError = (error: unknown) => {
    if (!(error instanceof Error)) {
      return '无法打开摄像头，请重试。';
    }

    switch (error.name) {
      case 'NotAllowedError':
      case 'PermissionDeniedError':
        return '浏览器已拒绝摄像头或麦克风访问，请检查站点权限设置。';
      case 'NotFoundError':
      case 'DevicesNotFoundError':
        return '未检测到可用的摄像头或麦克风设备。';
      case 'NotReadableError':
      case 'TrackStartError':
        return '摄像头正在被其他应用占用，请关闭占用后重试。';
      case 'OverconstrainedError':
      case 'ConstraintNotSatisfiedError':
        return '当前设备不支持所请求的摄像头参数。';
      case 'SecurityError':
        return '当前页面环境不允许访问摄像头，请确认使用 HTTPS 或受信任环境。';
      default:
        return `打开摄像头失败：${error.message || error.name}`;
    }
  };

  const refreshVideoInputs = async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter((device) => device.kind === 'videoinput');
      setVideoInputs(cameras);
      if (!selectedVideoDeviceId && cameras.length > 0) {
        setSelectedVideoDeviceId(cameras[0].deviceId);
      }
    } catch (error) {
      console.warn('Failed to enumerate video devices:', error);
    }
  };

  const withMediaTimeout = async <T,>(promise: Promise<T>, timeoutMs = 4000) => {
    return await Promise.race<T>([
      promise,
      new Promise<T>((_, reject) => {
        window.setTimeout(() => {
          reject(new Error('Media request timed out'));
        }, timeoutMs);
      })
    ]);
  };

  const buildEmbeddedCameraHint = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('electron') || userAgent.includes('trae')) {
      return '当前内嵌浏览器的摄像头接口没有正常返回，请优先在系统浏览器中打开页面，或检查应用容器是否放行媒体采集。';
    }
    return '当前环境的摄像头接口未正常返回，请尝试切换设备、关闭其他占用摄像头的应用，或改用系统浏览器打开。';
  };

  // ---------------- WebRTC：把本地摄像头推给对方，并播放对方的画面 ----------------
  // HTTP 模式下信令通过 /online/signal 中转（客户端 3 秒轮询房间状态时取回）
  const sendSignalMessage = (
    type: 'offer' | 'answer' | 'candidate' | 'bye',
    payload: unknown,
    to: string
  ) => {
    if (transportMode === 'socket' && socket) {
      socket.emit('signal', { roomId, to, type, payload });
      return;
    }
    if (!sessionId) return;
    void sendOnlineSignal(sessionId, roomId, type, payload, to).catch(() => undefined);
  };

  const attachRemoteStream = (peerSessionId: string, stream: MediaStream) => {
    remoteStreamsRef.current.set(peerSessionId, stream);
    const element = remoteVideoRef1.current;
    if (element && element.srcObject !== stream) {
      element.srcObject = stream;
      void element.play().catch(() => undefined);
    }
    setRemoteConnected(true);
  };

  const closePeer = (peerSessionId: string) => {
    const pc = peerConnections.current.get(peerSessionId);
    if (pc) {
      try { pc.close(); } catch { /* 忽略 */ }
    }
    peerConnections.current.delete(peerSessionId);
    remoteStreamsRef.current.delete(peerSessionId);
    pendingCandidatesRef.current.delete(peerSessionId);
    offeredPeersRef.current.delete(peerSessionId);
    if (remoteVideoRef1.current) remoteVideoRef1.current.srcObject = null;
    setRemoteConnected(false);
  };

  const flushPendingCandidates = async (peerSessionId: string, pc: RTCPeerConnection) => {
    const queued = pendingCandidatesRef.current.get(peerSessionId);
    if (!queued || queued.length === 0) return;
    pendingCandidatesRef.current.delete(peerSessionId);
    for (const candidate of queued) {
      try { await pc.addIceCandidate(candidate); } catch { /* 忽略单条候选失败 */ }
    }
  };

  const ensurePeer = (peerSessionId: string): RTCPeerConnection => {
    const existing = peerConnections.current.get(peerSessionId);
    if (existing) return existing;

    const pc = new RTCPeerConnection({
      iceServers: [{ urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'] }],
    });
    const stream = localStream.current;
    if (stream) {
      stream.getTracks().forEach((track) => {
        try { pc.addTrack(track, stream); } catch { /* 忽略重复轨道 */ }
      });
    }
    pc.onicecandidate = (event) => {
      if (!event.candidate) return;
      const candidate = typeof event.candidate.toJSON === 'function' ? event.candidate.toJSON() : event.candidate;
      sendSignalMessage('candidate', candidate, peerSessionId);
    };
    pc.ontrack = (event) => {
      const remote = event.streams[0] || new MediaStream([event.track]);
      attachRemoteStream(peerSessionId, remote);
    };
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'connected') setRemoteConnected(true);
      if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
        remoteStreamsRef.current.delete(peerSessionId);
        setRemoteConnected(false);
      }
    };
    peerConnections.current.set(peerSessionId, pc);
    return pc;
  };

  const startOfferTo = async (peerSessionId: string) => {
    if (!peerSessionId || offeredPeersRef.current.has(peerSessionId)) return;
    offeredPeersRef.current.add(peerSessionId);
    try {
      const pc = ensurePeer(peerSessionId);
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      sendSignalMessage('offer', { type: offer.type, sdp: offer.sdp }, peerSessionId);
    } catch (error) {
      offeredPeersRef.current.delete(peerSessionId);
      console.error('create offer failed:', error);
    }
  };

  const handleSignalMessage = async (message: OnlineSignalMsg) => {
    if (!message || !message.id || !message.from) return;
    if (processedSignalIdsRef.current.has(message.id)) return;
    processedSignalIdsRef.current.add(message.id);
    if (message.from === sessionId) return;

    try {
      if (message.type === 'offer') {
        const pc = ensurePeer(message.from);
        await pc.setRemoteDescription(new RTCSessionDescription(message.payload));
        await flushPendingCandidates(message.from, pc);
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        sendSignalMessage('answer', { type: answer.type, sdp: answer.sdp }, message.from);
      } else if (message.type === 'answer') {
        const pc = peerConnections.current.get(message.from);
        if (!pc) return;
        if (pc.signalingState !== 'stable') {
          await pc.setRemoteDescription(new RTCSessionDescription(message.payload));
          await flushPendingCandidates(message.from, pc);
        }
      } else if (message.type === 'candidate') {
        const pc = peerConnections.current.get(message.from);
        if (!pc || !pc.remoteDescription) {
          const queued = pendingCandidatesRef.current.get(message.from) || [];
          queued.push(message.payload);
          pendingCandidatesRef.current.set(message.from, queued);
        } else {
          await pc.addIceCandidate(message.payload);
        }
      } else if (message.type === 'bye') {
        closePeer(message.from);
      }
    } catch (error) {
      console.error('handle signal failed:', error);
    }
  };

  useEffect(() => {
    if (chatContainerRef.current) {
      setTimeout(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
      }, 100);
    }
  }, [chat]);

  useEffect(() => {
    if (transportMode !== 'socket' || !socket) return;

    socket.emit('join-room', { roomId, password: roomPassword, user: { name: userDisplayName, email: userEmail } });

    socket.on('room-error', (msg: string) => {
      alert(msg);
      onBackRef.current();
    });

    // WebRTC 信令（socket 模式由服务端直接转发）
    socket.on('signal', (message: OnlineSignalMsg) => {
      void handleSignalMessage(message);
    });

    socket.on('room-users', (roomUsers: OnlineRoomUser[]) => {
      setUsers(roomUsers);
      const me = roomUsers.find(u => u.email === userEmail);
      if (me) {
        setMyRole(me.role);
        setAmIHost(Boolean(me.isHost));
      }
      finishPendingAction('role');
    });

    socket.on('room-chat', (msgs: OnlineChatMsg[]) => {
      setChat([...msgs]);
    });

    socket.on('battle-started', ({ active, votes }: { active: boolean; votes: Record<string, string> }) => {
      setBattleActive(active);
      finishPendingAction('start');
    });

    socket.on('room-deleted', ({ roomId: deletedRoomId }: { roomId: string }) => {
      if (deletedRoomId !== roomId) return;
      if (deleteRoomInProgressRef.current) return;
      deleteRoomInProgressRef.current = true;
      alert('房主已离开，房间已解散，正在返回联机大厅。');
      onBackRef.current();
    });

    return () => {
      socket.off('room-error');
      socket.off('signal');
      socket.off('room-users');
      socket.off('room-chat');
      socket.off('battle-started');
      socket.off('battle-votes');
      socket.off('battle-ended');
      socket.off('room-deleted');
      if (!deleteRoomInProgressRef.current) {
        socket.emit('leave-room', { roomId });
      }
      if (localStream.current) {
        localStream.current.getTracks().forEach(track => track.stop());
      }
      peerConnections.current.forEach(pc => pc.close());
    };
  }, [roomId, roomPassword, socket, transportMode, userDisplayName, userEmail]);

  // 摄像头就绪 + 场上有第二名选手时建立 WebRTC（约定 sessionId 较小的一方发 offer，避免互相打断）
  useEffect(() => {
    if (!sessionId || !cameraActive || !localStream.current || myRole !== 'PLAYER') return;
    users
      .filter((entry) => entry.role === 'PLAYER' && entry.sessionId && entry.sessionId !== sessionId)
      .forEach((other) => {
        if (sessionId < other.sessionId) void startOfferTo(other.sessionId);
      });
  }, [cameraActive, users, myRole, sessionId]);

  // 关摄像头时通知对方并断开
  useEffect(() => {
    if (cameraActive) return;
    peerConnections.current.forEach((_pc, peerSessionId) => {
      sendSignalMessage('bye', null, peerSessionId);
      closePeer(peerSessionId);
    });
  }, [cameraActive]);

  useEffect(() => {
    if (transportMode !== 'http' || !sessionId) return;

    let cancelled = false;
    let joined = false;
    let intervalId: number | null = null;

    const syncRoomState = async () => {
      if (cancelled || !joined) return;
      try {
        const payload = await getOnlineRoomState(sessionId, { ...user, email: userEmail, displayName: userDisplayName }, roomId);
        if (cancelled) return;
        const roomUsers = payload.roomUsers || [];
        setUsers(roomUsers);
        setChat(payload.roomChat || []);
        const me = roomUsers.find(entry => entry.email === userEmail);
        // 有操作在等待服务器确认时，跳过这轮轮询返回的旧值，避免把乐观更新覆盖回去。
        if (me && actionInFlightRef.current !== 'role') {
          setMyRole(me.role);
          setAmIHost(Boolean(me.isHost));
        }
        setBattleActive(Boolean(payload.battleActive));
        // WebRTC 信令（offer/answer/candidate）随轮询一起取回
        (payload.signals || []).forEach((message) => {
          void handleSignalMessage(message);
        });
      } catch (error) {
        if (cancelled || !joined) return;
        // 只提示一次并退回大厅，避免轮询把同一个错误弹窗无限刷屏。
        joined = false;
        if (intervalId !== null) window.clearInterval(intervalId);
        alert((error as Error).message || 'Failed to sync room state.');
        onBackRef.current();
      }
    };
    roomRefreshRef.current = syncRoomState;

    void joinOnlineRoom(sessionId, { ...user, email: userEmail, displayName: userDisplayName }, roomId, roomPassword)
      .then((payload) => {
        if (cancelled) return;
        // 加入成功（含密码校验通过）之后才开始轮询，避免"加入失败仍进入房间"。
        joined = true;
        setUsers(payload.roomUsers || []);
        setChat(payload.roomChat || []);
        const me = (payload.roomUsers || []).find(entry => entry.email === userEmail);
        if (me) {
          setMyRole(me.role);
          setAmIHost(Boolean(me.isHost));
        }
        setBattleActive(Boolean(payload.battleActive));
        (payload.signals || []).forEach((message) => {
          void handleSignalMessage(message);
        });
        intervalId = window.setInterval(() => {
          void syncRoomState();
        }, 3000);
      })
      .catch((error) => {
        if (!cancelled) {
          alert((error as Error).message || 'Failed to join room.');
          onBackRef.current();
        }
      });

    return () => {
      cancelled = true;
      joined = false;
      if (roomRefreshRef.current === syncRoomState) roomRefreshRef.current = null;
      if (intervalId !== null) window.clearInterval(intervalId);
      // 离开房间前通知对方关闭连接
      peerConnections.current.forEach((_pc, peerSessionId) => {
        sendSignalMessage('bye', null, peerSessionId);
      });
      if (!deleteRoomInProgressRef.current) {
        void leaveOnlineRoom(sessionId, roomId).catch(() => undefined);
      }
      if (localStream.current) {
        localStream.current.getTracks().forEach(track => track.stop());
      }
      peerConnections.current.forEach(pc => pc.close());
    };
  }, [roomId, roomPassword, sessionId, transportMode, userUid, userDisplayName, userEmail]);

  // Handle camera changes based on role changes independently from socket connection
  useEffect(() => {
    void refreshVideoInputs();

    if (!navigator.mediaDevices?.addEventListener) return;
    const handleDeviceChange = () => {
      void refreshVideoInputs();
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
    };
  }, [selectedVideoDeviceId]);

  useEffect(() => {
    const startCamera = async () => {
      let requestPending = true;
      let timeoutId: number | null = null;
      try {
        setCameraStarting(true);
        setCameraError(null);
        // #region debug-point A:start-camera
        reportDebugEvent('A', 'VideoBattle.tsx:startCamera', 'camera start requested', {
          myRole,
          selectedVideoDeviceId: selectedVideoDeviceId || null,
          hadExistingStream: Boolean(localStream.current)
        });
        // #endregion
        if (navigator.permissions?.query) {
          const cameraPermission = await navigator.permissions.query({ name: 'camera' as PermissionName }).catch(() => null);
          const micPermission = await navigator.permissions.query({ name: 'microphone' as PermissionName }).catch(() => null);
          // #region debug-point D:permissions
          reportDebugEvent('D', 'VideoBattle.tsx:startCamera', 'media permissions checked', {
            cameraPermission: cameraPermission?.state ?? 'unsupported',
            microphonePermission: micPermission?.state ?? 'unsupported',
            userAgent: navigator.userAgent
          });
          // #endregion
        }
        timeoutId = window.setTimeout(() => {
          if (!requestPending) return;
          // #region debug-point D:getusermedia-timeout
          reportDebugEvent('D', 'VideoBattle.tsx:startCamera', 'getUserMedia still pending after timeout', {
            myRole,
            selectedVideoDeviceId: selectedVideoDeviceId || null,
            userAgent: navigator.userAgent
          });
          // #endregion
        }, 4000);
        if (localStream.current) {
          localStream.current.getTracks().forEach(track => track.stop());
          localStream.current = null;
        }
        let stream: MediaStream | null = null;
        const preferredVideoConstraints = selectedVideoDeviceId
          ? { deviceId: { exact: selectedVideoDeviceId } }
          : true;
        try {
          stream = await withMediaTimeout(
            navigator.mediaDevices.getUserMedia({
              video: preferredVideoConstraints,
              audio: false
            }),
            4000
          );
        } catch (preferredError) {
          // #region debug-point D:video-only-preferred-failed
          reportDebugEvent('D', 'VideoBattle.tsx:startCamera', 'preferred video-only request failed, retrying with generic camera', {
            errorName: preferredError instanceof Error ? preferredError.name : 'unknown',
            errorMessage: preferredError instanceof Error ? preferredError.message : String(preferredError),
            selectedVideoDeviceId: selectedVideoDeviceId || null
          });
          // #endregion
          stream = await withMediaTimeout(
            navigator.mediaDevices.getUserMedia({
              video: true,
              audio: false
            }),
            4000
          );
        }
        requestPending = false;
        if (timeoutId !== null) {
          window.clearTimeout(timeoutId);
        }
        localStream.current = stream;
        // #region debug-point A:stream-created
        reportDebugEvent('A', 'VideoBattle.tsx:startCamera', 'getUserMedia resolved', {
          streamId: stream.id,
          videoTracks: stream.getVideoTracks().map((track) => ({
            id: track.id,
            label: track.label,
            enabled: track.enabled,
            muted: track.muted,
            readyState: track.readyState,
            settings: typeof track.getSettings === 'function' ? track.getSettings() : null
          })),
          audioTracks: stream.getAudioTracks().map((track) => ({
            id: track.id,
            label: track.label,
            enabled: track.enabled,
            muted: track.muted,
            readyState: track.readyState
          }))
        });
        // #endregion
        await bindStreamToVideo(myVideoRef.current, stream);
        await refreshVideoInputs();
        try {
          const audioStream = await withMediaTimeout(
            navigator.mediaDevices.getUserMedia({ audio: true, video: false }),
            2500
          );
          audioStream.getAudioTracks().forEach((track) => stream?.addTrack(track));
          // #region debug-point D:audio-appended
          reportDebugEvent('D', 'VideoBattle.tsx:startCamera', 'audio track appended after video preview started', {
            audioTrackCount: stream.getAudioTracks().length
          });
          // #endregion
        } catch (audioError) {
          // #region debug-point D:audio-optional-failed
          reportDebugEvent('D', 'VideoBattle.tsx:startCamera', 'optional audio request failed, keeping video-only preview', {
            errorName: audioError instanceof Error ? audioError.name : 'unknown',
            errorMessage: audioError instanceof Error ? audioError.message : String(audioError)
          });
          // #endregion
        }
      } catch (err) {
        requestPending = false;
        if (timeoutId !== null) {
          window.clearTimeout(timeoutId);
        }
        console.error("Camera error:", err);
        setCameraActive(false);
        const fallbackMessage = err instanceof Error && err.message === 'Media request timed out'
          ? `摄像头请求超时。${buildEmbeddedCameraHint()}`
          : formatCameraError(err);
        setCameraError(fallbackMessage);
        // #region debug-point A:start-camera-failed
        reportDebugEvent('A', 'VideoBattle.tsx:startCamera', 'getUserMedia rejected', {
          errorName: err instanceof Error ? err.name : 'unknown',
          errorMessage: err instanceof Error ? err.message : String(err)
        });
        // #endregion
      } finally {
        setCameraStarting(false);
      }
    };

    if (myRole !== 'AUDIENCE' && cameraActive) {
      startCamera();
    } else {
      setCameraStarting(false);
      // #region debug-point C:clear-stream
      reportDebugEvent('C', 'VideoBattle.tsx:cameraEffect', 'clearing local stream or unbinding video', {
        myRole,
        cameraActive,
        hadExistingStream: Boolean(localStream.current),
        hasVideoElement: Boolean(myVideoRef.current)
      });
      // #endregion
      if (localStream.current) {
        localStream.current.getTracks().forEach(track => track.stop());
        localStream.current = null;
      }
      if (myVideoRef.current) {
        void bindStreamToVideo(myVideoRef.current, null);
      }
      if (myRole === 'AUDIENCE') {
        setCameraError(null);
      }
    }
  }, [myRole, cameraActive, selectedVideoDeviceId]);

  useEffect(() => {
    if (!myVideoRef.current || !localStream.current || !cameraActive || myRole === 'AUDIENCE') {
      // #region debug-point C:skip-rebind
      reportDebugEvent('C', 'VideoBattle.tsx:rebindEffect', 'rebind skipped', {
        hasVideoElement: Boolean(myVideoRef.current),
        hasLocalStream: Boolean(localStream.current),
        cameraActive,
        myRole
      });
      // #endregion
      return;
    }
    // #region debug-point C:rebind-stream
    reportDebugEvent('C', 'VideoBattle.tsx:rebindEffect', 'rebind effect running', {
      streamId: localStream.current.id,
      myRole,
      cameraActive
    });
    // #endregion
    void bindStreamToVideo(myVideoRef.current, localStream.current);
  }, [cameraActive, myRole]);

  const handleRoleChange = (role: OnlineRole) => {
    if (myRole === role || pendingAction === 'role') return;
    const previousRole = myRole;
    // 先本地生效，再等服务器确认，避免弱网下点击后长时间没有反应。
    setMyRole(role);
    beginPendingAction('role');
    if (transportMode === 'http' && sessionId) {
      void changeOnlineRole(sessionId, roomId, role)
        .then((payload) => {
          setUsers(payload.roomUsers || []);
          const me = (payload.roomUsers || []).find(entry => entry.email === userEmail);
          if (me) {
            setMyRole(me.role);
            setAmIHost(Boolean(me.isHost));
          }
          // 顺手刷新一次，让"开始比赛"按钮的可用状态立刻反映最新人数。
          void roomRefreshRef.current?.();
        })
        .catch((error) => {
          setMyRole(previousRole);
          alert((error as Error).message || 'Failed to change role.');
        })
        .finally(endPendingAction);
      return;
    }
    socket?.emit('change-role', { roomId, role });
  };

  const handleSendChat = (e: React.FormEvent) => {
    e.preventDefault();
    if (!msgInput.trim()) return;
    if (transportMode === 'http' && sessionId) {
      void sendOnlineRoomChat(sessionId, user, roomId, msgInput)
        .then((payload) => {
          setChat(payload.roomChat || []);
          setMsgInput('');
        })
        .catch((error) => {
          alert((error as Error).message || 'Failed to send chat.');
        });
      return;
    }
    socket?.emit('send-chat', { roomId, message: msgInput, sender: userDisplayName });
    setMsgInput('');
  };

  const handleExitRoom = () => {
    setShowExitConfirm(false);
    onBackRef.current();
  };

  const players = users.filter(u => u.role === 'PLAYER');
  const canStartBattle = players.length === 2 || (Boolean(user.isAdmin) && players.length === 1);

  const handleStartBattle = () => {
    recordStartProbe('handleStartBattle');
    if (!canStartBattle || pendingAction !== null) return;
    setActionError(null);
    beginPendingAction('start');
    if (transportMode === 'http' && sessionId) {
      void startOnlineBattle(sessionId, roomId)
        .then((payload) => {
          setUsers(payload.roomUsers || []);
          setBattleActive(Boolean(payload.battleActive));
          setActionError(null);
        })
        .catch((error) => {
          const message = (error as Error).message || 'Failed to start battle.';
          setActionError(`开始比赛失败 / Failed to start battle: ${message}`);
          // 服务端可能已经开局成功（上一次请求超时），刷新一次以跟上真实状态
          if (/already in progress/i.test(message)) {
            void roomRefreshRef.current?.();
          }
        })
        .finally(endPendingAction);
      return;
    }
    socket?.emit('start-battle', { roomId, isAdmin: Boolean(user.isAdmin) });
  };

  // 对战中选手掉线/不足 2 人时，房主可以手动结束对战，让房间恢复可操作状态。
  const handleResetBattle = () => {
    if (pendingAction !== null) return;
    if (transportMode !== 'http' || !sessionId) return;
    setActionError(null);
    beginPendingAction('reset');
    void resetOnlineBattle(sessionId, roomId)
      .then((payload) => {
        setUsers(payload.roomUsers || []);
        setBattleActive(Boolean(payload.battleActive));
      })
      .catch((error) => {
        setActionError(`结束对战失败 / Failed to reset the battle: ${(error as Error).message || 'unknown error'}`);
      })
      .finally(endPendingAction);
  };

  const inviteLink = buildRoomInviteLink(roomId, roomPassword || undefined);

  const handleOpenInvite = async () => {
    setInviteCopied(false);
    setShowInvite(true);
    try {
      await navigator.clipboard.writeText(inviteLink);
      setInviteCopied(true);
    } catch {
      setInviteCopied(false);
    }
  };

  const handleCopyInvite = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setInviteCopied(true);
    } catch {
      setInviteCopied(false);
    }
  };

  const handleShareToWeChat = async () => {
    await handleCopyInvite();
    alert('微信分享即将上线，邀请链接已复制，请到微信中粘贴发送给好友。');
  };

  return (
    <div className="h-full w-full flex flex-col items-center justify-start relative overflow-y-auto p-4 md:p-8 text-white font-rajdhani">
      <div className="w-full max-w-6xl mb-6 md:mb-8 z-50">
        <div className="flex items-center justify-between gap-3">
          <button onClick={() => setShowExitConfirm(true)} className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors uppercase tracking-widest font-orbitron text-xs md:text-sm">
            <ArrowLeft size={16} /> BACK
          </button>
          {myRole !== 'AUDIENCE' && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowCameraMenu(v => !v)}
                className="flex items-center gap-2 rounded-xl border border-zinc-700 bg-zinc-900/60 px-3 py-2 font-orbitron text-xs uppercase tracking-[0.12em] text-zinc-300 hover:bg-zinc-800 transition-colors"
              >
                <Video size={16} /> 摄像头
              </button>
              {showCameraMenu && (
                <div className="absolute right-0 top-full mt-2 w-64 rounded-xl border border-zinc-700 bg-zinc-950/95 p-3 shadow-[0_0_20px_rgba(0,0,0,0.5)] z-50">
                  <div className="font-orbitron text-[10px] tracking-widest uppercase text-zinc-500 mb-2">选择摄像头</div>
                  <div className="max-h-44 overflow-y-auto space-y-1 mb-2">
                    {videoInputs.length === 0 ? (
                      <button type="button" className="w-full text-left px-2 py-1.5 rounded text-sm text-zinc-500">默认摄像头</button>
                    ) : (
                      videoInputs.map((device, index) => (
                        <button
                          key={device.deviceId || index}
                          type="button"
                          onClick={() => { setSelectedVideoDeviceId(device.deviceId); setShowCameraMenu(false); }}
                          className={`w-full text-left px-2 py-1.5 rounded text-sm transition-colors ${selectedVideoDeviceId === device.deviceId ? 'bg-blue-600/20 text-blue-300' : 'text-zinc-300 hover:bg-zinc-800'}`}
                        >
                          {device.label || `Camera ${index + 1}`}
                        </button>
                      ))
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setCameraError(null);
                      setCameraActive(false);
                      setShowCameraMenu(false);
                      window.setTimeout(() => setCameraActive(true), 0);
                    }}
                    disabled={cameraStarting}
                    className={`w-full rounded-lg px-3 py-2 text-xs font-orbitron uppercase tracking-[0.12em] transition-colors ${cameraStarting ? 'bg-zinc-800 text-zinc-500 cursor-not-allowed' : 'bg-blue-600/20 border border-blue-500/40 text-blue-300 hover:bg-blue-600/35'}`}
                  >
                    重试摄像头
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Room members bar (moved to top) */}
      <div className="w-full max-w-6xl mb-6 z-10">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 bg-zinc-900/50 border border-zinc-800 rounded-xl px-3 py-2">
          <span className="flex items-center gap-2 text-zinc-500 font-orbitron text-xs tracking-widest uppercase shrink-0">
            <Users size={14} /> Room ({users.length})
          </span>
          {users.length === 0 ? (
            <span className="text-zinc-600 text-sm font-rajdhani">暂无成员</span>
          ) : (
            users.map(u => (
              <span key={u.id} className={`text-sm font-rajdhani font-semibold ${u.role === 'PLAYER' ? 'text-blue-400' : 'text-purple-400'}`}>
                {u.name}
                {u.isHost && <span className="ml-1 text-[10px] font-orbitron text-amber-400 border border-amber-500/40 px-1 rounded align-middle">房主</span>}
              </span>
            ))
          )}
        </div>
      </div>

      {/* 聊天/邀请弹层是 z-115/116 的全屏遮罩，会吃掉按钮点击：弹层打开时直接隐藏按钮 */}
      {amIHost && !battleActive && !showChat && !showInvite && !showExitConfirm && (
        // 固定在底部：房间内成员信息刷新时页面高度会变，按钮跟着上下跳动容易点空
        <div className="fixed inset-x-0 bottom-24 z-[105] flex justify-center px-4 pointer-events-none">
          <button
            onClick={() => {
              recordStartProbe('click');
              handleStartBattle();
            }}
            onPointerDown={(event) => {
              recordStartProbe('pointerdown');
              pressStartRef.current = { x: event.clientX, y: event.clientY };
            }}
            onPointerUp={(event) => {
              recordStartProbe('pointerup');
              const start = pressStartRef.current;
              pressStartRef.current = null;
              if (!start) return;
              const moved = Math.hypot(event.clientX - start.x, event.clientY - start.y);
              if (moved > 12) return;
              handleStartBattle();
            }}
            onPointerCancel={() => {
              pressStartRef.current = null;
            }}
            disabled={!canStartBattle || pendingAction === 'start'}
            className={`pointer-events-auto w-full max-w-xl flex items-center justify-center gap-2 py-4 rounded-xl font-orbitron font-bold tracking-[0.15em] text-sm md:text-base transition-all border touch-manipulation shadow-xl backdrop-blur ${
              canStartBattle
                ? 'bg-red-600/80 border-red-400/60 text-white hover:bg-red-600'
                : 'bg-zinc-900/90 border-zinc-700 text-zinc-500 cursor-not-allowed'
            } ${pendingAction === 'start' ? 'opacity-70 cursor-wait' : ''}`}
          >
            {pendingAction === 'start' && <Loader2 size={16} className="animate-spin" />}
            {pendingAction === 'start'
              ? '正在开始比赛… / STARTING'
              : players.length === 2
                ? '开始比赛 / START BATTLE'
                : (Boolean(user.isAdmin) && players.length === 1)
                  ? '开始比赛（生成虚拟选手） / START WITH BOT'
                  : '需要2名选手才能开始 / NEED 2 PLAYERS'}
          </button>
        </div>
      )}

      {actionError && (
        <div className="relative z-40 w-full max-w-6xl mb-4 rounded-xl border border-red-500/50 bg-red-950/70 px-4 py-3 text-xs md:text-sm text-red-200 font-rajdhani flex items-start justify-between gap-3">
          <span className="break-words">{actionError}</span>
          <button
            type="button"
            onClick={() => setActionError(null)}
            className="shrink-0 text-red-300 hover:text-white transition-colors"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
      )}

      {battleActive && players.length < 2 && (
        <div className="relative z-30 w-full max-w-6xl mb-6 bg-zinc-900/70 border border-amber-500/40 rounded-2xl p-4 md:p-5 text-center">
          <div className="font-orbitron text-sm md:text-base tracking-[0.14em] uppercase text-amber-300">
            对战已中断 / BATTLE INTERRUPTED
          </div>
          <p className="mt-2 text-xs md:text-sm text-zinc-300 font-rajdhani">
            房间内选手不足 2 名，对战无法继续。房主可结束对战重新开局。
          </p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
            {(amIHost || user.isAdmin) && (
              <button
                type="button"
                onClick={handleResetBattle}
                disabled={pendingAction !== null}
                className="px-4 py-2 rounded-xl border border-blue-500/50 bg-blue-600/20 text-blue-300 hover:bg-blue-600/35 font-orbitron text-xs md:text-sm disabled:opacity-60"
              >
                {pendingAction === 'reset' ? '正在结束对战…' : '结束对战 / END BATTLE'}
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowExitConfirm(true)}
              className="px-4 py-2 rounded-xl border border-zinc-700 bg-zinc-900 text-zinc-300 hover:bg-zinc-800 font-orbitron text-xs md:text-sm"
            >
              退出房间 / LEAVE ROOM
            </button>
          </div>
        </div>
      )}

      <div className={`flex flex-col lg:flex-row w-full max-w-6xl gap-6 flex-1 ${amIHost && !battleActive ? 'pb-32' : ''}`}>
        {/* Left Side: Videos */}
        <div className="flex-1 flex flex-col gap-4 relative z-40 pointer-events-auto">
          {/* Roles Selection (比赛期间隐藏) */}
          {!battleActive && (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 flex flex-wrap items-center gap-2 md:gap-4 justify-center">
               <button onClick={() => handleRoleChange('PLAYER')} disabled={pendingAction === 'role'} className={`px-2 md:px-4 py-2 rounded uppercase font-orbitron text-[10px] md:text-xs flex-1 md:flex-none whitespace-nowrap disabled:cursor-wait ${myRole === 'PLAYER' ? 'bg-blue-500 text-black font-bold shadow-[0_0_10px_#3b82f6]' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'} ${pendingAction === 'role' ? 'opacity-70' : ''}`}>Be Player / 选手</button>
               <button onClick={() => handleRoleChange('AUDIENCE')} disabled={pendingAction === 'role'} className={`px-2 md:px-4 py-2 rounded uppercase font-orbitron text-[10px] md:text-xs flex-1 md:flex-none whitespace-nowrap disabled:cursor-wait ${myRole === 'AUDIENCE' ? 'bg-purple-500 text-black font-bold shadow-[0_0_10px_#a855f7]' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'} ${pendingAction === 'role' ? 'opacity-70' : ''}`}>Audience / 观众</button>
               {pendingAction === 'role' && (
                 <span className="flex items-center gap-1.5 text-[10px] md:text-xs font-orbitron uppercase tracking-widest text-cyan-300">
                   <Loader2 size={12} className="animate-spin" /> 同步身份中 / SYNCING
                 </span>
               )}
            </div>
          )}

          {/* Battle PK view or regular videos */}
          {battleActive && players.length === 2 ? (
            <div className="flex flex-col gap-4">
              <div className="relative grid grid-cols-2 gap-0">
                {players.map((p) => {
                  const isMe = Boolean(sessionId) ? p.sessionId === sessionId : p.email === user.email;
                  return (
                    <div key={p.id} className="bg-black border border-zinc-800 rounded-xl overflow-hidden relative aspect-[3/4] flex flex-col">
                      {isMe ? (
                        <video ref={myVideoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
                      ) : remoteConnected ? (
                        <video ref={remoteVideoRef1} autoPlay playsInline className="w-full h-full object-cover" />
                      ) : (
                        <div className="flex-1 flex flex-col items-center justify-center gap-2 bg-zinc-950 px-3 text-center">
                          <Loader2 size={18} className="animate-spin text-zinc-600" />
                          <span className="text-zinc-500 font-orbitron text-[10px] md:text-xs leading-5">等待对方画面…<br />WAITING FOR PEER</span>
                        </div>
                      )}
                      <div className="absolute bottom-2 left-2 bg-black/60 px-2 py-1 rounded text-[10px] md:text-xs z-10">
                        {p.name}{isMe ? ' (你)' : ''}
                      </div>
                    </div>
                  );
                })}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <span className="font-orbitron text-2xl md:text-4xl font-bold text-white drop-shadow-[0_0_10px_rgba(168,85,247,0.8)]">VS</span>
                </div>
              </div>

              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 flex flex-col items-center gap-3">
                <div className="text-zinc-300 font-orbitron text-xs tracking-widest uppercase">对战进行中 / BATTLE IN PROGRESS</div>
                <div className="text-[11px] md:text-xs text-zinc-500 font-rajdhani text-center">
                  {remoteConnected ? '已连接对方画面 / Peer connected' : '正在建立视频连接… / Connecting video…'}
                </div>
                <div className="w-full flex gap-2">
                  {players.map((p) => (
                    <div key={p.id} className="flex-1 h-2 rounded-full bg-zinc-800 overflow-hidden">
                      <div className={`h-full ${remoteConnected ? 'bg-emerald-500' : 'bg-zinc-700'}`} style={{ width: remoteConnected ? '100%' : '30%' }} />
                    </div>
                  ))}
                </div>
                {(amIHost || user.isAdmin) && (
                  <button
                    type="button"
                    onClick={handleResetBattle}
                    disabled={pendingAction !== null}
                    className="px-4 py-2 rounded-xl border border-blue-500/50 bg-blue-600/20 text-blue-300 hover:bg-blue-600/35 font-orbitron text-xs md:text-sm disabled:opacity-60"
                  >
                    {pendingAction === 'reset' ? '正在结束…' : '结束对战 / END BATTLE'}
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {myRole !== 'AUDIENCE' && (
                <div className="bg-black border border-zinc-800 rounded-xl overflow-hidden relative aspect-[3/4] flex flex-col items-center justify-center pointer-events-auto z-50">
                  {cameraActive ? (
                     <video ref={myVideoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
                  ) : (
                    <div className="flex flex-col items-center justify-center gap-4">
                      <span className="text-zinc-600 font-orbitron">{cameraStarting ? 'STARTING CAMERA...' : 'CAMERA OFF'}</span>
                      <button 
                        onClick={() => setCameraActive(true)}
                        disabled={cameraStarting}
                        className={`px-4 py-2 rounded-lg font-orbitron text-xs md:text-sm transition-colors flex items-center gap-2 relative z-50 pointer-events-auto ${
                          cameraStarting ? 'bg-zinc-800 text-zinc-500 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-500 text-white'
                        }`}
                      >
                        <Video size={16} /> {cameraStarting ? 'STARTING...' : 'START CAMERA'}
                      </button>
                    </div>
                  )}
                  {cameraError && (
                    <div className="absolute left-3 right-3 bottom-10 rounded-xl border border-red-500/40 bg-red-950/80 px-3 py-2 text-xs md:text-sm text-red-200 backdrop-blur-sm z-40">
                      <div className="font-orbitron tracking-widest uppercase text-[10px] md:text-xs mb-1">Camera Error</div>
                      <div className="font-rajdhani leading-5">{cameraError}</div>
                    </div>
                  )}
                  {cameraActive && (
                     <button 
                       onClick={() => {
                         setCameraActive(false);
                         setCameraError(null);
                       }}
                       className="absolute top-2 right-2 bg-red-600/80 hover:bg-red-500 text-white px-2 py-1 rounded text-[10px] md:text-xs font-bold uppercase shadow-[0_0_10px_#dc2626] z-50 pointer-events-auto"
                     >
                       STOP
                     </button>
                  )}
                  <div className="absolute bottom-2 left-2 bg-black/60 px-2 py-1 rounded text-[10px] md:text-xs z-10">Me ({myRole})</div>
                </div>
              )}
              
              {/* Displaying Remote Players (simplified placeholders for P2P connection logic) */}
              {users.filter(u => u.role === 'PLAYER' && u.email !== user.email).map((player, idx) => (
                <div key={player.id} className="bg-black border border-zinc-800 rounded-xl overflow-hidden relative aspect-[3/4] flex items-center justify-center pointer-events-auto z-50">
                   <video autoPlay playsInline className="w-full h-full object-cover" />
                   <div className="absolute bottom-2 left-2 bg-black/60 px-2 py-1 rounded text-[10px] md:text-xs z-10">{player.name} (PLAYER)</div>
                </div>
              ))}
            </div>
          )}

        </div>

      </div>

      {/* Invite + Chat floating buttons */}
      {!showChat && (
        <div className="fixed bottom-5 right-5 z-[110] flex flex-col items-end gap-3">
          <button
            type="button"
            onClick={handleOpenInvite}
            className="flex items-center gap-2 rounded-full bg-purple-600 hover:bg-purple-500 text-white px-4 py-3 shadow-[0_0_20px_rgba(147,51,234,0.4)] font-orbitron text-sm uppercase tracking-[0.1em] transition-colors"
          >
            <UserPlus size={18} /> 邀请
          </button>
          <button
            type="button"
            onClick={() => setShowChat(true)}
            className="flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 text-white px-4 py-3 shadow-[0_0_20px_rgba(37,99,235,0.4)] font-orbitron text-sm uppercase tracking-[0.1em] transition-colors"
          >
            <MessageSquare size={18} /> 聊天
          </button>
        </div>
      )}

      {/* Invite popup */}
      {showInvite && (
        <div
          className="fixed inset-0 z-[116] flex items-center justify-center bg-black/60 p-4"
          onClick={() => setShowInvite(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm bg-zinc-950/95 border border-zinc-700 rounded-2xl p-5 shadow-[0_0_30px_rgba(147,51,234,0.15)]"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-orbitron text-zinc-300 text-sm uppercase tracking-widest flex items-center gap-2">
                <UserPlus size={16} /> 邀请好友
              </h3>
              <button type="button" onClick={() => setShowInvite(false)} className="text-zinc-500 hover:text-white transition-colors">
                <X size={18} />
              </button>
            </div>
            <p className="text-zinc-500 text-sm mb-4 font-rajdhani">复制链接发给好友，对方点击后自动打开 App 并进入本房间。</p>
            <div className="bg-black/60 border border-zinc-800 rounded-lg px-3 py-2 mb-4">
              <p className="text-xs text-zinc-400 break-all font-mono">{inviteLink}</p>
            </div>
            <div className="flex items-center justify-center gap-8">
              <button type="button" onClick={handleShareToWeChat} className="flex flex-col items-center gap-1.5 group">
                <span className="w-12 h-12 rounded-full bg-[#07C160] group-hover:bg-[#06AD56] flex items-center justify-center shadow-[0_0_16px_rgba(7,193,96,0.35)] transition-colors">
                  <WeChatIcon className="w-6 h-6 text-white" />
                </span>
                <span className="text-xs text-zinc-400">微信</span>
              </button>
              <button type="button" onClick={handleCopyInvite} className="flex flex-col items-center gap-1.5 group">
                <span className="w-12 h-12 rounded-full bg-zinc-800 group-hover:bg-zinc-700 flex items-center justify-center border border-zinc-700 transition-colors">
                  <Copy size={20} className="text-zinc-300" />
                </span>
                <span className="text-xs text-zinc-400">{inviteCopied ? '已复制' : '复制链接'}</span>
              </button>
            </div>
            {inviteCopied && <p className="mt-3 text-center text-[11px] text-emerald-400">邀请链接已复制</p>}
          </div>
        </div>
      )}

      {/* Chat popup */}
      {showChat && (
        <div
          className="fixed inset-0 z-[115] flex items-end sm:items-center justify-center bg-black/60"
          onClick={() => setShowChat(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full sm:w-[380px] sm:max-w-[90vw] bg-zinc-950/95 border border-zinc-700 rounded-t-2xl sm:rounded-2xl flex flex-col h-[70vh] max-h-[600px] sm:h-[520px] shadow-[0_0_30px_rgba(59,130,246,0.15)]"
          >
            <div className="flex items-center justify-between border-b border-zinc-800 px-4 py-3 shrink-0">
              <h3 className="font-orbitron text-zinc-400 text-xs tracking-widest uppercase flex items-center gap-2">
                <MessageSquare size={14} /> Chat
              </h3>
              <button
                type="button"
                onClick={() => setShowChat(false)}
                className="text-zinc-500 hover:text-white text-sm font-orbitron uppercase tracking-widest"
              >
                关闭
              </button>
            </div>
            <div ref={chatContainerRef} className="flex-1 overflow-y-auto space-y-3 p-4 custom-scrollbar">
              {chat.length === 0 ? (
                <div className="text-center text-zinc-600 py-4 font-rajdhani text-sm">Say hi to the room! / 跟房间里的人打个招呼吧！</div>
              ) : (
                chat.map((msg, i) => (
                  <div key={i} className="text-sm bg-black/40 rounded-lg p-2 border border-zinc-800/50">
                    <div className="flex justify-between items-end mb-1">
                      <span className={`font-bold ${msg.sender === user.displayName ? 'text-purple-400' : 'text-blue-400'}`}>{msg.sender}</span>
                      <span className="text-[10px] text-zinc-600 font-mono">{new Date(msg.time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                    </div>
                    <span className="text-zinc-300 font-rajdhani break-words whitespace-pre-wrap">{msg.text}</span>
                  </div>
                ))
              )}
            </div>
            <form onSubmit={handleSendChat} className="flex gap-2 p-3 border-t border-zinc-800 shrink-0">
              <input
                type="text"
                value={msgInput}
                onChange={e => setMsgInput(e.target.value)}
                placeholder="Type a message... / 发送消息..."
                className="flex-1 bg-black border border-zinc-700 rounded-xl px-4 py-2 text-base font-rajdhani text-white focus:outline-none focus:border-blue-500 transition-colors"
              />
              <button
                type="submit"
                disabled={!msgInput.trim()}
                className={`px-4 py-2 rounded-xl text-xs font-orbitron transition-colors ${msgInput.trim() ? 'bg-blue-600 text-white hover:bg-blue-500 shadow-[0_0_10px_rgba(37,99,235,0.3)]' : 'bg-zinc-800 text-zinc-500 cursor-not-allowed'}`}
              >
                Send
              </button>
            </form>
          </div>
        </div>
      )}

      {showExitConfirm && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/75 px-4">
          <div className="w-full max-w-md rounded-2xl border border-zinc-700 bg-zinc-950/95 p-5 shadow-[0_0_30px_rgba(59,130,246,0.15)]">
            <div className="font-orbitron text-base md:text-lg uppercase tracking-[0.14em] text-zinc-200">
              退出房间
            </div>
            <p className="mt-3 text-sm md:text-base text-zinc-300 leading-6">
              {amIHost ? '你是房主，退出后房间将被解散。确定要退出房间吗？' : '确定要退出房间吗？'}
            </p>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setShowExitConfirm(false)}
                className="rounded-xl border border-zinc-700 bg-zinc-900 px-4 py-3 text-sm font-orbitron uppercase tracking-[0.12em] text-zinc-300 transition-colors hover:bg-zinc-800"
              >
                取消
              </button>
              <button
                type="button"
                onClick={handleExitRoom}
                className="rounded-xl border border-blue-500/50 bg-blue-600/20 px-4 py-3 text-sm font-orbitron uppercase tracking-[0.12em] text-blue-300 transition-colors hover:bg-blue-600/35"
              >
                确认退出
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
